/**
 * ManifestRefreshService
 *
 * Owns retrieval of manifest.json for the web client. It is used once at boot
 * (fetchManifest) and then runs a background refresh loop so long-lived sessions
 * keep seeing new observed/live products (MRMS radar, satellite, lightning, QPE,
 * RTMA) that the observed pipelines publish every 5-10 minutes.
 *
 * Behavior:
 * - Re-fetches the manifest every `ui.live_weather.manifest_refresh_interval_sec`
 *   seconds (config.yaml, default 300; 0 disables background refresh).
 * - Pauses while the document is hidden and, when
 *   `ui.live_weather.manifest_refresh_on_resume` is true, re-fetches immediately
 *   when the tab becomes visible or the network comes back online and the last
 *   successful fetch is older than the interval.
 * - Compares a content hash of the response body and only publishes to the store
 *   when the manifest actually changed. Background refreshes publish ONLY the
 *   `manifest` key; user selections and boot-time defaults are never re-applied.
 * - Network errors keep the current manifest in place.
 */
import store from '../state';
import { Manifest } from '../types';

const DEFAULT_REFRESH_INTERVAL_SEC = 300;

interface RefreshConfig {
  intervalMs: number;
  refreshOnResume: boolean;
}

function readRefreshConfig(): RefreshConfig {
  const lw = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather) || {};
  const rawSec = Number(lw.manifest_refresh_interval_sec);
  const sec = Number.isFinite(rawSec) && rawSec >= 0 ? rawSec : DEFAULT_REFRESH_INTERVAL_SEC;
  return {
    intervalMs: sec * 1000,
    refreshOnResume: lw.manifest_refresh_on_resume !== false
  };
}

/** FNV-1a 32-bit hash; fast enough for multi-megabyte manifest bodies. */
function hashText(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${text.length}:${(h >>> 0).toString(16)}`;
}

export class ManifestRefreshService {
  private static timerId: ReturnType<typeof setInterval> | null = null;
  private static inFlight: Promise<boolean> | null = null;
  private static lastHash: string | null = null;
  private static lastFetchMs = 0;
  private static listenersBound = false;
  private static config: RefreshConfig = readRefreshConfig();

  /**
   * Fetch manifest.json using the host-aware source priority
   * (production apex -> gatekeeper, staging -> bundled, local dev -> tiles/).
   * Returns the raw body text so callers can hash it before parsing.
   */
  private static async fetchManifestText(): Promise<string> {
    let response: Response;
    const bust = () => `_t=${Date.now()}`;
    const host = window.location.hostname;
    const isProductionApex = (host === 'skyskout.com' || host === 'www.skyskout.com' || host === 'goldenwindow.app' || host === 'www.goldenwindow.app');
    const isStaging = host.startsWith('stage.') || host.startsWith('staging.') || host.startsWith('test.') || host.endsWith('.pages.dev');

    if (isProductionApex) {
      try {
        response = await fetch(`https://auth.skyskout.com/manifest.json?${bust()}`);
        if (!response.ok) {
          response = await fetch(`manifest.json?${bust()}`);
        }
      } catch {
        response = await fetch(`manifest.json?${bust()}`);
      }
    } else if (isStaging) {
      // Staging environment: prioritize the bundled local manifest.json deployed with this preview release
      try {
        response = await fetch(`manifest.json?${bust()}`);
        if (!response.ok) {
          response = await fetch(`https://auth.skyskout.com/manifest-staging.json?${bust()}`);
          if (!response.ok) {
            response = await fetch(`https://auth.skyskout.com/manifest.json?${bust()}`);
          }
        }
      } catch {
        response = await fetch(`https://auth.skyskout.com/manifest.json?${bust()}`);
      }
    } else {
      // Local dev / test environment (localhost or LAN IP)
      try {
        response = await fetch(`tiles/manifest.json?${bust()}`);
        if (!response.ok) {
          response = await fetch(`manifest.json?${bust()}`);
        }
      } catch {
        response = await fetch(`manifest.json?${bust()}`);
      }
    }

    if (!response.ok) {
      throw new Error('Failed to load manifest.json configuration');
    }
    return response.text();
  }

  /**
   * Boot-time fetch. Records the content hash so the first background refresh
   * does not republish an identical manifest.
   */
  public static async fetchManifest(): Promise<Manifest> {
    const text = await this.fetchManifestText();
    const manifest: Manifest = JSON.parse(text);
    this.lastHash = hashText(text);
    this.lastFetchMs = Date.now();
    return manifest;
  }

  /**
   * Re-fetch the manifest and publish it to the store if its content changed.
   * Resolves true when a new manifest was published.
   */
  public static refreshNow(): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = (async () => {
      try {
        const text = await this.fetchManifestText();
        this.lastFetchMs = Date.now();
        const hash = hashText(text);
        if (hash === this.lastHash) {
          return false;
        }
        const manifest: Manifest = JSON.parse(text);
        this.lastHash = hash;
        console.log('[ManifestRefresh] New manifest detected; publishing updated observed/forecast availability.');
        store.update({ manifest });
        return true;
      } catch (err) {
        console.warn('[ManifestRefresh] Background manifest refresh failed; keeping current manifest:', err);
        return false;
      } finally {
        this.inFlight = null;
      }
    })();
    return this.inFlight;
  }

  /** Start the background refresh loop (idempotent). */
  public static start(): void {
    this.config = readRefreshConfig();
    if (this.config.intervalMs <= 0) {
      console.log('[ManifestRefresh] Background manifest refresh disabled (interval = 0).');
      return;
    }
    this.bindListeners();
    if (typeof document === 'undefined' || !document.hidden) {
      this.startTimer();
    }
  }

  /** Stop the background refresh loop. */
  public static stop(): void {
    this.stopTimer();
  }

  private static startTimer(): void {
    if (this.timerId !== null || this.config.intervalMs <= 0) return;
    this.timerId = setInterval(() => {
      void this.refreshNow();
    }, this.config.intervalMs);
  }

  private static stopTimer(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private static refreshIfStale(): void {
    if (!this.config.refreshOnResume) return;
    if (Date.now() - this.lastFetchMs >= this.config.intervalMs) {
      void this.refreshNow();
    }
  }

  private static bindListeners(): void {
    if (this.listenersBound || typeof document === 'undefined') return;
    this.listenersBound = true;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopTimer();
      } else {
        this.refreshIfStale();
        this.startTimer();
      }
    });

    window.addEventListener('online', () => {
      if (!document.hidden) {
        this.refreshIfStale();
      }
    });
  }
}

export default ManifestRefreshService;
